import React, {useCallback} from 'react'
import Link from 'next/link'

import ShoppingCartLineIcon from 'remixicon-react/ShoppingCartLineIcon'
import styled from 'styled-components'
import classNames from 'classnames'

import {CheckoutLine, VariantList} from '@festi/common/api/rest'
import {useCheckout, useEvents} from '@festi/common/contexts'
import {styledTheme} from '@festi/common/themes'
import useTagmanager from '@festi/common/utils/tagmanager'
import {ProductPrices} from '@festi/common/components/cart/ProductPrices'
import {AugmentedUserPrice} from '@festi/common/utils/rest'
import settings from '@festi/common/constants/settings'
import {onDutyFree} from '@festi/common/constants/channels'
import {NewActionButton} from '@festi/common/components/buttons'
import ProductImage from './utilities/ProductImage'

const CardWrapper = styled.div`
  display: grid;
  grid-template-columns: 100px auto;
  gap: 20px;
  width: 100%;
  height: 120px;
  padding-block: 10px;

  &.small {
    gap: 12px;
    height: 68px;
  }
`

const ProductLink = styled(Link)`
  width: 100%;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${styledTheme.palette.lightBlue};
    outline-offset: 2px;
    border-radius: 12px;
  }
`

const ImageWrapper = styled.div`
  height: 100px;

  &.small {
    height: 68px;
  }
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const TitleContainer = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Title = styled.p`
  font-size: 16px;
  line-height: 1.4;
  font-weight: 500;
  color: ${styledTheme.palette.ui80Solid};

  &.small {
    font-size: 14px;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 4px;
`

interface Props {
  variant: VariantList
  origin: string
  position: number
  small?: boolean
  addToCart?: boolean
  userPrice: AugmentedUserPrice
}

export default function HorizontalProductCard({
  variant,
  origin,
  position,
  small = false,
  addToCart = false,
  userPrice,
}: Props) {
  const {sendClickEvent} = useEvents()
  const {sendEcommerceClickEvent} = useTagmanager()

  const price =
    userPrice?.price?.price ||
    variant?.listings?.[settings.channel]?.price?.price ||
    0
  const lowestPrice =
    userPrice?.lowestPrice ||
    variant?.listings?.[settings.channel]?.price?.lowestPrice ||
    0
  const discountedPrice =
    userPrice?.userPrice ||
    variant?.listings?.[settings.channel]?.price?.discountedPrice ||
    0

  const handleClick = useCallback(() => {
    // TODO: Combine events for google analytics and algolia
    sendEcommerceClickEvent(variant, position, origin)

    sendClickEvent('clickedObjectIDsAfterSearch', origin, variant.sku, position)
  }, [origin, variant, position, sendClickEvent, sendEcommerceClickEvent])

  return (
    <ProductLink
      href={`/vorur/${variant.product.slug}/${variant.sku}`}
      onClick={handleClick}
    >
      <CardWrapper className={classNames({small})}>
        <ImageWrapper className={classNames({small})}>
          <ProductImage
            src={variant.firstImage?.image?.webpS}
            jpg={variant.firstImage?.image?.jpgS}
            alt={variant.firstImage?.alt}
            compact
          />
        </ImageWrapper>
        <InfoWrapper>
          <TitleContainer>
            <Title className={classNames({small})}>{variant.name}</Title>
          </TitleContainer>
          <PriceWrapper>
            <ProductPrices
              price={price}
              lowestPrice={lowestPrice}
              discountedPrice={discountedPrice}
              size={'20px'}
              oldPriceSize={'16px'}
            />
          </PriceWrapper>
          {addToCart && <CardAddToCartButton variant={variant} />}
        </InfoWrapper>
      </CardWrapper>
    </ProductLink>
  )
}

const ButtonRoot = styled.div`
  width: 100%;
  padding-block: 4px;
`

interface CardAddToCartButtonProps {
  variant: VariantList
}

function CardAddToCartButton({variant}: CardAddToCartButtonProps) {
  const {restCheckout, restCheckoutLoading, cartIsUpdating, cartUpdate} =
    useCheckout()
  const {sendAddToCartEvent} = useEvents()
  const {sendEcommerceAddToCartEvent} = useTagmanager()

  const {id, sku} = variant

  const qtyInCart =
    restCheckout?.lines.find((line: CheckoutLine) => line.variant.sku === sku)
      ?.quantity || 0

  const addToCart = useCallback(() => {
    sendEcommerceAddToCartEvent([variant], origin)
    cartUpdate({
      [id]: qtyInCart + 1,
    })

    sendAddToCartEvent(
      'convertedObjectIDsAfterSearch',
      'Product Added to Cart',
      variant.sku || '',
    )
  }, [
    id,
    qtyInCart,
    variant,
    cartUpdate,
    sendAddToCartEvent,
    sendEcommerceAddToCartEvent,
  ])

  return (
    <ButtonRoot>
      <NewActionButton
        icon={ShoppingCartLineIcon}
        iconSize={16}
        small
        disabled={
          restCheckoutLoading ||
          cartIsUpdating ||
          !(onDutyFree ? variant?.isInStockDutyfree : variant?.isInStockWeb)
        }
        onClick={(e) => {
          e.preventDefault()
          addToCart()
        }}
      >
        Setja í körfu
      </NewActionButton>
    </ButtonRoot>
  )
}
